import React, {useEffect} from 'react';
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import {ArrowBack} from "@mui/icons-material";
import {StyledTableCell, StyledTableRow} from "../../components/Event/EventTable";
import moment from "moment-timezone";
import {useGetEventPilotRegistrationsQuery} from "../../slices/apiSlice";
import {
    competitionRegistrationStatusColor,
    competitionRegistrationStatusLabel,
    countryArray, eventStatus, isViableRegistrationStatus, registrationStatus
} from "../../util/standards-util";
import {Alert, Chip, Dialog, DialogContent, Stack} from "@mui/material";
import {findFlagUrlByIso2Code} from 'country-flags-svg';
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import PilotInfo2 from "./PilotInfo2";
import Typography from "@mui/material/Typography";
import EventRegistrationStatusWidget from "../../components/Event/EventRegistrationStatusWidget";
import RefreshPilotRankingsWidget from "../../components/Event/RefreshPilotRankingsWidget";
import DownloadEntryListWidget from "../../components/Event/DownloadEntryListWidget";
import {SGP} from "../Competition/sgp-results-react";
import {getEventDatesString} from "../Calendar";
import Button from "@mui/material/Button";

export const normalize = (v) => {
    return v > 0 ? 1 : v < 0 ? -1 : 0
}

export const mapRegistrationStatusToComparable  = (u)=> {
    switch(u) {
        case registrationStatus.STATUS_OK : return 2;
        case registrationStatus.STATUS_CANCELED : return 1;
        case registrationStatus.STATUS_BANNED : return 0;
        case registrationStatus.STATUS_SUBMITTED : return 2;
        case registrationStatus.STATUS_PRELIMINARY : return 2;
        case registrationStatus.STATUS_PENDING : return 2;
        case registrationStatus.STATUS_REJECTED : return 1;
        case registrationStatus.STATUS_APPROVED : return 2;
        case registrationStatus.STATUS_RESUBMITTED : return 1;
        case registrationStatus.STATUS_ON_PROBATION : return 0;
        case registrationStatus.STATUS_WITHDRAWN : return 1;
    }
}

export const sortEntriesPerSgpRules = (entriesArray, event) => {
    const eventCountry = event.country
    const registrationCutoff = event.registrationCloseDate ? moment(event.registrationCloseDate) : undefined
    let entries = [...entriesArray].sort((a,b) => {
        const aStatus = mapRegistrationStatusToComparable(a.registration.u)
        const bStatus = mapRegistrationStatusToComparable(b.registration.u)

        if (aStatus !== bStatus) return normalize(bStatus - aStatus)

        //See if we have one of the two being a wildcard
        if (
            (
                a.registration.n === 'wildcard' ||
                b.registration.n === 'wildcard'
            ) &&
            a.registration.n !== b.registration.n
        ) {
            if (a.registration.n === 'wildcard') return -1
            else return 1
        }

        //Status equal, so compare rank
        const aRank = Number(a.registration.m)
        const bRank = Number(b.registration.m)
        const aAfterCuttoff = registrationCutoff ? moment(a.registration.r).isAfter(registrationCutoff,'day'): false
        const bAfterCuttoff = registrationCutoff ? moment(b.registration.r).isAfter(registrationCutoff,'day'): false

        if (aAfterCuttoff || bAfterCuttoff) {
            if (aAfterCuttoff && bAfterCuttoff) {
                return normalize(moment(a.registration.r).unix() - moment(b.registration.r).unix())
            }
            else {
                return bAfterCuttoff ? -1 : 1
            }
        }

        if (aRank > 0 && bRank > 0) {
            //we can compare ranks
            return normalize( aRank - bRank )
        }
        else if (aRank>0) {
            return -1
        }
        else if (bRank>0) {
            return 1
        }
        else {
            //no rank info, so compare according to date registered
            return normalize(moment(a.registration.r).unix() - moment(b.registration.r).unix())
        }
    })

    const wildcards = []
    const foreignBerths = []
    const others = []
    const foreignBerthNumber = 5

    entries.map((entry) => {
        if (entry.registration.n === 'wildcard') {
            wildcards.push(entry)
        }
        else if (eventCountry && mapRegistrationStatusToComparable(entry.registration.u) === 2 && entry.pilot.nationality !== eventCountry && foreignBerths.length < foreignBerthNumber && (!registrationCutoff || !moment(entry.registration.r).isAfter(registrationCutoff,'day'))){
            foreignBerths.push(entry)
        }
        else {
            others.push(entry)
        }
    })

    return [].concat(wildcards,foreignBerths,others)
}

export const isWildcardAvailable = (entriesArray) => {
    return entriesArray.find(entry => entry.registration.n === 'wildcard')  ? false : true
}

const PilotRegistrationList = ({event, hasEventManagerScope}) => {

    const eventId = event.id
    const eventCountry = event.country
    const eventEntryNo = 20
    const [mode, setMode] = React.useState('list')
    const [pilotData, setPilotData] = React.useState()
    const [entries, setEntries] = React.useState()
    const [pilotRlId, setPilotRlId] = React.useState()
    const [hasWildcardAvailable, setHasWildcardAvailable] = React.useState()

    const {data, isSuccess} =
        useGetEventPilotRegistrationsQuery({eventId:eventId})

    const handleEditClicked = (e, c) => {
        e.stopPropagation()
        const id = e.target.value ? e.target.value : e.target.parentNode.value ? e.target.parentNode.value : e.target.parentNode.parentNode.value
    }

    const handlePilotClicked = (e,id) => {
        e.stopPropagation()
        setPilotRlId(id)
        setMode('rlPilotInfo')
    }

    const refreshRanking = () => {
        setMode('refreshRlData')
    }

    const onRefreshRankingClose = () => {
        setMode('list')
    }

    const handleSelectClicked = (e,id)=> {

        const entry = entries.find( e => e.registration.i === id)
        setPilotData(entry)

        setMode('pilotInfo')
    }

    const onPilotRegistrationUpdate = (newReg) => {
        const newEntries = entries.map( entry => {
            return entry.registration.i === newReg.i ?
                {
                    registration: newReg,
                    details: entry.details,
                    pilot: entry.pilot,
                }
                :
                entry
        })

        setHasWildcardAvailable(isWildcardAvailable(newEntries))
        setEntries(sortEntriesPerSgpRules(newEntries, event))
    }

    const onPilotDetailsUpdate = (newDetails) => {
        const newEntries = entries.map( entry => {
            return entry.details.i=== newDetails.i ?
                {
                    registration: entry.registration,
                    details: newDetails,
                    pilot: entry.pilot,
                }
                :
                entry
        })
        setEntries(sortEntriesPerSgpRules(newEntries, event))
    }

    useEffect(() => {
        if (isSuccess) {

            const entries = sortEntriesPerSgpRules(
                data.registrations.map((reg)=>{
                    return {
                        registration:reg,
                        details:data.details.find( d => d.i === reg.i),
                        pilot:data.pilots.find(p => p.id === reg.p),
                    }
                }),
                event
            )

            setHasWildcardAvailable(isWildcardAvailable(entries))
            setEntries(entries)
        }
    }, [isSuccess]);

    const onClosePilotInfo = () => {
        setMode('list')
    }

    const onCloseRlPilotInfo = () => {
        setMode('list')
        setPilotRlId()
    }


    return (
        <>
            <Box sx={{
                display:'flex',
                alignItems:'center',
                //justifyContent:'space-between',
                my:'1em',
                columnGap:'2em',
            }}>
                <Typography variant={'h2'} flexGrow={1} textAlign={'left'}>Pilots</Typography>
                { hasEventManagerScope && (event.status === eventStatus.STATUS_PLANNED || event.status === eventStatus.STATUS_PLANNED_REGISTRATION_CLOSED) &&
                    <EventRegistrationStatusWidget event={event}/>
                }
                { hasEventManagerScope && (event.status === eventStatus.STATUS_PLANNED || event.status === eventStatus.STATUS_PLANNED_REGISTRATION_CLOSED) && entries && entries.length>0 &&
                    <Button variant={'contained'} onClick={refreshRanking}>Refresh pilot ranking</Button>
                }
                { hasEventManagerScope &&
                    <DownloadEntryListWidget event={event}/>

                }
            </Box>
            <Box sx={{textAlign:'left',bgcolor:'#113248',color:'#ffffff'}} mb={3} p={2}>
                <Stack direction={'row'}>
                    <Box mr={'24px'}>
                        <img src='/etc/img/sgp-series-logo-v3.png' alt="SGP" style={{width:'160px'}}/>
                    </Box>
                    <Box>
                        <Typography variant={'h5'}>{SGP.getChampionshipTitle(event)}</Typography>
                        <Typography variant={'h5'}>{event.fullEditionTitle}</Typography>
                        <Typography variant={'h6'}>{event.venue}</Typography>
                        <Typography variant={'h6'}>{getEventDatesString(event)}</Typography>
                    </Box>
                </Stack>
            </Box>
            {entries && mode === 'list' &&
                <TableContainer component={'div'}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>#</StyledTableCell>
                                <StyledTableCell>Pilot</StyledTableCell>
                                <StyledTableCell sx={{textAlign:'center'}}>Nationality</StyledTableCell>
                                <StyledTableCell>Sailplane</StyledTableCell>
                                <StyledTableCell sx={{textAlign: 'right'}}>Ranking</StyledTableCell>
                                <StyledTableCell>Register date</StyledTableCell>
                                <StyledTableCell>Email</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry,idx) => {
                                    const reg= entry.registration
                                    const pilot = entry.pilot
                                    const details = entry.details

                                    const sx = idx < eventEntryNo && isViableRegistrationStatus(reg.u) ?
                                        {
                                            '&:last-child td, &:last-child th': {border: 0},
                                            "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                        }
                                        :
                                        {
                                            '&:last-child td, &:last-child th': {border: 0},
                                            '& td, & th': {borderBottom: 'solid 1px #999999'},
                                            "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                            '&:nth-of-type(even)': {
                                                backgroundColor:'#cccccc',
                                            },
                                            '&:nth-of-type(odd)': {
                                                backgroundColor:'#dddddd',
                                            },
                                        }


                                return (
                                        <StyledTableRow
                                            key={reg.i}
                                            sx={sx}
                                            onClick={(e) => {
                                                handleSelectClicked(e, reg.i)
                                            }}
                                        >
                                            <StyledTableCell component="th" scope="row">
                                                {idx < eventEntryNo  && isViableRegistrationStatus(reg.u) ? idx + 1 : '-' }
                                            </StyledTableCell>
                                            <StyledTableCell><Link underline={"none"} onClick={(e) => {handlePilotClicked(e,details.igcRankingId)}} sx={{cursor:'pointer'}}>{pilot.firstname} {pilot.lastname}</Link></StyledTableCell>
                                            <StyledTableCell sx={{textAlign:'center'}}><Tooltip title={countryArray.find( c => c.key===pilot.nationality).label} arrow><img src={findFlagUrlByIso2Code(pilot.nationality)} style={{height:'1.2em'}}/></Tooltip></StyledTableCell>
                                            <StyledTableCell>{details.gliderDescription}</StyledTableCell>
                                            <StyledTableCell sx={{textAlign: 'right'}}>{reg.m ? reg.m : '-'}</StyledTableCell>
                                            <StyledTableCell>{moment(reg.r).format("D MMM YYYY")}</StyledTableCell>
                                            <StyledTableCell>{details.email ? <Link href={`mailto:${details.email}`} underline="none" onClick={(evt) => {evt.stopPropagation()}}>{details.email}</Link> : ""}</StyledTableCell>
                                            <StyledTableCell><Chip label={competitionRegistrationStatusLabel[reg.u]} color={competitionRegistrationStatusColor[reg.u]} size={'small'}/></StyledTableCell>
                                        </StyledTableRow>
                                    )
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            }
            {entries && mode === 'list' && entries.length === 0 &&
                <Alert severity={'info'} sx={{textAlign:'center'}}>There are no pilots registered for this event</Alert>
            }
            {isSuccess && mode === 'pilotInfo' &&
                <PilotInfo2 entry={pilotData} event={event} hasWildcardAvailable={hasWildcardAvailable} onClose={onClosePilotInfo} onPilotRegistrationUpdate={onPilotRegistrationUpdate} onPilotDetailsUpdate={onPilotDetailsUpdate}/>
            }
            { mode === 'rlPilotInfo' &&
                <Dialog onClose={onCloseRlPilotInfo} open={true} fullWidth maxWidth="lg">
                    <DialogContent sx={{height:'80vh',display:'flex',flexDirection:'column',alignContent:'center'}}>
                        <Box sx={{
                            display:'flex',
                            alignItems:'center',
                            //justifyContent:'space-between',
                            my:'1em',
                            columnGap:'2em',
                        }}>
                            <IconButton onClick={()=>{onCloseRlPilotInfo()}}><ArrowBack/></IconButton>
                            <Typography variant={'h2'} flexGrow={1} textAlign={'left'}>Pilot profile</Typography>
                        </Box>
                        <Box flexGrow={1}>
                            <iframe src={`https://rankingdata.fai.org/SGP_displaypilotdets.php?pilotid=${pilotRlId}`}
                                    height={'100%'}
                                    width={'100%'}
                                    style={{border:'none'}}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            }
            { mode === 'refreshRlData' &&
                <RefreshPilotRankingsWidget event={event} entries={entries} onClose={onRefreshRankingClose}/>
            }
        </>
    )
}

export default PilotRegistrationList